import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  TrendUp,
  ArrowLeft,
  Download,
  ChatCircleDots,
  Target,
  Lightning,
  Warning,
  CheckCircle,
  ArrowUp,
  ArrowDown,
  Minus
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/context/LanguageContext';
import Logo from '@/components/Logo';
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell
} from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportPage = () => {
  const { assessmentId } = useParams();
  const { getAuthHeaders } = useAuth();
  const { t, language } = useLanguage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [generating, setGenerating] = useState(
    new URLSearchParams(window.location.search).get('generating') === '1'
  );

  // Detect whether AI-generated sections are still empty (report being built)
  const isAiPending = (r) => {
    if (!r) return true;
    if (!r.executive_summary || !r.executive_summary.trim()) return true;
    const sw = r.swot_analysis || {};
    const noSwot = !sw.strengths && !sw.weaknesses && !sw.opportunities && !sw.threats && !sw.raw_text;
    const rm = r.roadmap || {};
    const noRoadmap = !rm.immediate && !rm.short_term && !rm.medium_term && !rm.raw_text;
    return noSwot || noRoadmap;
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessmentId]);

  // Auto-refresh while AI is generating (up to 4 minutes)
  useEffect(() => {
    if (!generating) return;
    if (report && !isAiPending(report)) {
      setGenerating(false);
      // strip ?generating=1 from URL
      const url = new URL(window.location.href);
      url.searchParams.delete('generating');
      window.history.replaceState({}, '', url.toString());
      return;
    }
    const start = Date.now();
    // Poll aggressively for the first 30s (every 2.5s), then back off to 6s
    // so the user sees content as soon as the backend finishes the AI calls.
    const tick = () => {
      const elapsed = Date.now() - start;
      if (elapsed > 240000) { // give up after 4 min
        setGenerating(false);
        return;
      }
      fetchReport();
    };
    tick(); // immediate first re-fetch (fast in case backend was already ready)
    const fastTimer = setInterval(tick, 2500);
    const cutoffTimer = setTimeout(() => clearInterval(fastTimer), 30000);
    const slowTimer = setTimeout(() => {
      const id = setInterval(tick, 6000);
      // store on window so the cleanup function below can clear it
      window.__growthriq_slow_poll = id;
    }, 30000);
    return () => {
      clearInterval(fastTimer);
      clearTimeout(cutoffTimer);
      clearTimeout(slowTimer);
      if (window.__growthriq_slow_poll) {
        clearInterval(window.__growthriq_slow_poll);
        window.__growthriq_slow_poll = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, report]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/assessments/${assessmentId}/report`, {
        headers: getAuthHeaders()
      });
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
      if (!generating) toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API_URL}/reports/${assessmentId}/download`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `assessment_report_${assessmentId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('report.pdf_success'));
    } catch (error) {
      console.error('Failed to download PDF:', error);
      toast.error(t('report.pdf_not_available'));
    } finally {
      setDownloading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#16A34A';
    if (score >= 60) return '#0F1B36';
    if (score >= 40) return '#CA8A04';
    return '#E60000';
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Average';
    return 'Needs Improvement';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-[#52525B]">{t('report.generating')}</div>
      </div>
    );
  }

  // While AI is still generating in the background, the first /report fetch
  // typically 404's because the row hasn't been created yet. DO NOT show
  // "Rapor bulunamadı" in that case — show the generating screen and let the
  // polling effect (above) catch up. Without this guard the user briefly sees
  // a misleading "not found" panel and clicks "Dashboard" out of confusion.
  // Also covers the new iter30 flow where a placeholder Report row exists
  // with `full_report_json={}` while LLMs run in the background.
  const reportEmpty = report && (!report.full_report_json || Object.keys(report.full_report_json).length === 0);
  if ((!report && generating) || (reportEmpty && generating)) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6" data-testid="report-generating-screen">
        <div className="text-center max-w-md">
          <div className="inline-block w-12 h-12 border-4 border-[#3CD9B8] border-t-transparent rounded-full animate-spin mb-6" />
          <h2 className="text-xl font-bold text-[#0F1B36] mb-2">{t('report.generating')}</h2>
          <p className="text-sm text-[#52525B] leading-relaxed">
            {language === 'tr'
              ? 'AI değerlendirme raporunuzu hazırlıyor. SWOT, GAP analizi ve 30/60/90 günlük aksiyon planını oluşturmak yaklaşık 30-60 saniye sürer. Bu sayfayı açık tutun, otomatik güncellenir.'
              : 'AI is preparing your assessment report. Generating the SWOT, GAP analysis and 30/60/90-day action plan takes about 30-60 seconds. Keep this page open — it refreshes automatically.'}
          </p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#52525B] mb-4">{t('report.not_found')}</p>
          <Link to="/dashboard" className="text-[#0F1B36] hover:text-[#00227A]">
            {t('nav.dashboard')}
          </Link>
        </div>
      </div>
    );
  }

  const reportData = report.full_report_json || {};
  const categoryScores = reportData.category_scores || {};
  const overallScore = reportData.overall_score || 0;

  // Prepare radar chart data
  const radarData = Object.entries(categoryScores).map(([name, score]) => ({
    category: name.split(' ')[0], // Shorten for display
    fullName: name,
    score: score,
    fullMark: 100
  }));

  // Prepare bar chart data
  const barData = Object.entries(categoryScores)
    .map(([name, score]) => ({
      name: name.length > 15 ? name.substring(0, 15) + '...' : name,
      fullName: name,
      score: score
    }))
    .sort((a, b) => b.score - a.score);

  // Parse SWOT if it's a string
  let swotData = reportData.swot_analysis || {};
  if (swotData.raw_text) {
    try {
      swotData = JSON.parse(swotData.raw_text);
    } catch {
      swotData = { strengths: [], weaknesses: [], opportunities: [], threats: [] };
    }
  }

  // Parse roadmap
  let roadmapData = reportData.roadmap || {};
  if (roadmapData.raw_text) {
    try {
      roadmapData = JSON.parse(roadmapData.raw_text);
    } catch {
      roadmapData = { immediate: [], short_term: [], medium_term: [] };
    }
  }

  // Parse GAP analysis — support both new (current/target/gap) and legacy
  // (current_state/desired_state/gap_description/closing_actions) schemas
  // so old + new reports both render.
  let gapData = reportData.gap_analysis || [];
  if (gapData.raw_text) {
    try {
      gapData = JSON.parse(gapData.raw_text);
    } catch {
      gapData = [];
    }
  }
  if (!Array.isArray(gapData)) gapData = [];
  const normalizedGap = gapData.map((g) => ({
    category: g.category || g.name || '—',
    current_state: g.current_state || g.current || g.current_score || '',
    desired_state: g.desired_state || g.target || g.desired || '',
    gap_description: g.gap_description || g.gap || g.description || '',
    impact: g.impact || g.priority_reason || '',
    priority: g.priority || g.severity || '',
    closing_actions: Array.isArray(g.closing_actions)
      ? g.closing_actions
      : Array.isArray(g.actions)
      ? g.actions
      : [],
  }));

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-[#52525B] hover:text-[#09090B]">
              <ArrowLeft size={24} />
            </Link>
            <div className="flex items-center gap-2">
              <Logo variant="light" size="md" />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/advisor" className="btn-secondary flex items-center gap-2" data-testid="chat-advisor-btn">
              <ChatCircleDots size={20} />
              {t('report.chat_advisor')}
            </Link>
            <Button 
              onClick={handleDownloadPdf}
              disabled={downloading}
              className="bg-[#0F1B36] hover:bg-[#00227A] text-white rounded-none" 
              data-testid="download-pdf-btn"
            >
              <Download size={20} className="mr-2" />
              {downloading ? t('report.downloading') : t('report.download')}
            </Button>
          </div>
        </div>
      </header>

      {/* Report Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {generating && (
          <div className="mb-6 bg-[#FEF3C7] border border-[#F59E0B]/30 p-4 flex items-start gap-3" data-testid="report-generating-banner">
            <div className="w-5 h-5 mt-0.5 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-[#92400E]">{t('report.generating.title')}</p>
              <p className="text-sm text-[#78350F] mt-1">{t('report.generating.body')}</p>
            </div>
          </div>
        )}
        {/* Hero Score Section */}
        <div className="bg-white border border-[#E4E4E7] p-8 mb-8" data-testid="report-hero">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <p className="label-text text-[#0F1B36] mb-2">{t('report.title').toUpperCase()}</p>
              <h1 className="heading-2 text-[#09090B] mb-4">
                {reportData.company_profile?.name || 'Company'}
              </h1>
              <p className="text-[#52525B] mb-4">
                Generated on {new Date(reportData.generated_at || report.created_at).toLocaleDateString()}
              </p>
              <div className="flex items-center gap-4">
                <span className={`text-sm font-medium px-3 py-1`} style={{ 
                  backgroundColor: `${getScoreColor(overallScore)}15`,
                  color: getScoreColor(overallScore)
                }}>
                  {getScoreLabel(overallScore)}
                </span>
                <span className="text-[#52525B]">{reportData.company_profile?.industry}</span>
              </div>
            </div>
            <div className="text-center">
              <div 
                className="w-32 h-32 mx-auto rounded-full flex items-center justify-center border-8"
                style={{ borderColor: getScoreColor(overallScore) }}
              >
                <span className="text-4xl font-black" style={{ color: getScoreColor(overallScore) }}>
                  {overallScore}
                </span>
              </div>
              <p className="mt-4 text-[#52525B]">{t('report.overall_score')}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-white border border-[#E4E4E7] p-1 rounded-none">
            <TabsTrigger value="overview" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white">
              {t('report.tab.overview')}
            </TabsTrigger>
            <TabsTrigger value="categories" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white">
              {t('report.tab.categories')}
            </TabsTrigger>
            <TabsTrigger value="swot" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white">
              {t('report.tab.swot')}
            </TabsTrigger>
            <TabsTrigger value="roadmap" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white">
              {t('report.tab.roadmap')}
            </TabsTrigger>
            <TabsTrigger value="gap" className="rounded-none data-[state=active]:bg-[#0F1B36] data-[state=active]:text-white">
              {t('report.tab.gap')}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Executive Summary — magazine-style editorial framing */}
            <div
              className="relative bg-gradient-to-br from-[#0F1B36] via-[#132043] to-[#0F1B36] text-white overflow-hidden shadow-xl"
              data-testid="executive-summary"
            >
              {/* Decorative accent */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#3CD9B8]/10 rounded-full blur-3xl pointer-events-none"></div>
              <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-[#3CD9B8]/5 rounded-full blur-3xl pointer-events-none"></div>

              {/* Header strip */}
              <div className="relative flex items-center gap-3 px-8 pt-8 pb-4 border-b border-white/10">
                <div className="w-10 h-10 rounded-lg bg-[#3CD9B8] flex items-center justify-center shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0F1B36" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <path d="M14 2v6h6"/>
                    <path d="M16 13H8"/>
                    <path d="M16 17H8"/>
                    <path d="M10 9H8"/>
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#3CD9B8] font-semibold">
                    {language === 'tr' ? 'Bölüm 01 · Stratejik Değerlendirme' : 'Section 01 · Strategic Overview'}
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-0.5">
                    {t('report.executive_summary')}
                  </h2>
                </div>
              </div>

              {/* Body */}
              <div className="relative px-8 py-8 sm:px-12 sm:py-10">
                {reportData.executive_summary ? (
                  <div
                    className="max-w-4xl prose prose-invert prose-lg
                               prose-headings:text-white prose-headings:font-bold prose-headings:tracking-tight
                               prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-h2:pb-2 prose-h2:border-b prose-h2:border-white/15
                               prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2 prose-h3:text-[#3CD9B8]
                               prose-p:text-white/85 prose-p:leading-[1.75] prose-p:text-[15px] prose-p:my-4 prose-p:first:mt-0
                               prose-p:first-of-type:text-lg prose-p:first-of-type:leading-[1.7] prose-p:first-of-type:text-white prose-p:first-of-type:font-normal
                               prose-strong:text-white prose-strong:font-semibold
                               prose-em:text-[#3CD9B8] prose-em:not-italic prose-em:font-medium
                               prose-ul:my-4 prose-ul:pl-0 prose-ul:list-none prose-ul:space-y-2
                               prose-li:relative prose-li:pl-6 prose-li:text-white/85 prose-li:leading-relaxed prose-li:my-0
                               prose-li:before:content-[''] prose-li:before:absolute prose-li:before:left-0 prose-li:before:top-[0.7em] prose-li:before:w-2.5 prose-li:before:h-[2px] prose-li:before:bg-[#3CD9B8]
                               prose-ol:my-4 prose-ol:pl-6
                               prose-blockquote:border-l-4 prose-blockquote:border-[#3CD9B8] prose-blockquote:bg-white/[0.04] prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:my-6 prose-blockquote:not-italic prose-blockquote:text-white/90"
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {reportData.executive_summary}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-white/60 italic">{t('report.no_data')}</p>
                )}
              </div>

              {/* Signature footer */}
              <div className="relative px-8 pb-6 pt-2 border-t border-white/10 flex items-center justify-between text-[11px] text-white/50">
                <span className="tracking-wider uppercase">
                  {language === 'tr' ? 'GrowthRIQ · AI destekli değerlendirme' : 'GrowthRIQ · AI-powered assessment'}
                </span>
                <span className="tracking-wider uppercase">
                  {language === 'tr' ? 'Gizli · Yalnızca alıcı için' : 'Confidential · For recipient only'}
                </span>
              </div>
            </div>

            {/* Radar Chart */}
            <div className="bg-white border border-[#E4E4E7] p-8" data-testid="radar-chart">
              <h2 className="heading-3 text-[#09090B] mb-6">{t('report.category_scores')}</h2>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#E4E4E7" />
                    <PolarAngleAxis dataKey="category" tick={{ fill: '#52525B', fontSize: 12 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: '#52525B' }} />
                    <Radar
                      name="Score"
                      dataKey="score"
                      stroke="#0F1B36"
                      fill="#0F1B36"
                      fillOpacity={0.3}
                      strokeWidth={2}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-6">
            {/* Bar Chart */}
            <div className="bg-white border border-[#E4E4E7] p-8" data-testid="bar-chart">
              <h2 className="heading-3 text-[#09090B] mb-6">Category Scores</h2>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} layout="vertical" margin={{ left: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: '#52525B' }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#52525B', fontSize: 12 }} width={120} />
                    <Tooltip 
                      formatter={(value) => [`${value}%`, 'Score']}
                      contentStyle={{ border: '1px solid #E4E4E7', borderRadius: 0 }}
                    />
                    <Bar dataKey="score" radius={0}>
                      {barData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(categoryScores).map(([name, score]) => (
                <div key={name} className="bg-white border border-[#E4E4E7] p-6" data-testid={`category-${name}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#09090B]">{name}</h3>
                    <span 
                      className="text-2xl font-bold"
                      style={{ color: getScoreColor(score) }}
                    >
                      {score}
                    </span>
                  </div>
                  <div className="h-2 bg-[#F4F4F5] w-full">
                    <div 
                      className="h-full transition-all duration-500"
                      style={{ 
                        width: `${score}%`,
                        backgroundColor: getScoreColor(score)
                      }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-[#52525B]">{getScoreLabel(score)}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* SWOT Tab */}
          <TabsContent value="swot" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="swot-analysis">
              {/* Strengths */}
              <div className="bg-white border border-[#E4E4E7] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ArrowUp size={24} className="text-[#16A34A]" />
                  <h3 className="heading-4 text-[#09090B]">{t('report.strengths')}</h3>
                </div>
                <ul className="space-y-3">
                  {(swotData.strengths || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle size={18} weight="fill" className="text-[#16A34A] mt-0.5 flex-shrink-0" />
                      <span className="text-[#52525B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Weaknesses */}
              <div className="bg-white border border-[#E4E4E7] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ArrowDown size={24} className="text-[#E60000]" />
                  <h3 className="heading-4 text-[#09090B]">{t('report.weaknesses')}</h3>
                </div>
                <ul className="space-y-3">
                  {(swotData.weaknesses || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Warning size={18} weight="fill" className="text-[#E60000] mt-0.5 flex-shrink-0" />
                      <span className="text-[#52525B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Opportunities */}
              <div className="bg-white border border-[#E4E4E7] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Target size={24} className="text-[#0F1B36]" />
                  <h3 className="heading-4 text-[#09090B]">{t('report.opportunities')}</h3>
                </div>
                <ul className="space-y-3">
                  {(swotData.opportunities || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Lightning size={18} weight="fill" className="text-[#0F1B36] mt-0.5 flex-shrink-0" />
                      <span className="text-[#52525B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Threats */}
              <div className="bg-white border border-[#E4E4E7] p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Minus size={24} className="text-[#CA8A04]" />
                  <h3 className="heading-4 text-[#09090B]">{t('report.threats')}</h3>
                </div>
                <ul className="space-y-3">
                  {(swotData.threats || []).map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Warning size={18} className="text-[#CA8A04] mt-0.5 flex-shrink-0" />
                      <span className="text-[#52525B]">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* Roadmap Tab */}
          <TabsContent value="roadmap" className="space-y-6" data-testid="roadmap">
            {/* Immediate Actions (1-30 days) */}
            <div className="bg-white border border-[#E4E4E7] p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-[#E60000] flex items-center justify-center">
                  <span className="text-white font-bold text-sm">1-30</span>
                </div>
                <h3 className="heading-4 text-[#09090B]">{t('report.roadmap.immediate')}</h3>
              </div>
              <div className="space-y-4">
                {(roadmapData.immediate || []).map((action, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-[#F4F4F5] border-l-4 border-[#E60000]">
                    <span className="text-[#E60000] font-bold">{idx + 1}</span>
                    <div>
                      <p className="text-[#09090B] font-medium">{action.action || action}</p>
                      {action.impact && (
                        <p className="text-sm text-[#52525B] mt-1">Impact: {action.impact}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Short-term (31-60 days) */}
            <div className="bg-white border border-[#E4E4E7] p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-[#CA8A04] flex items-center justify-center">
                  <span className="text-white font-bold text-sm">31-60</span>
                </div>
                <h3 className="heading-4 text-[#09090B]">{t('report.roadmap.short_term')}</h3>
              </div>
              <div className="space-y-4">
                {(roadmapData.short_term || []).map((action, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-[#F4F4F5] border-l-4 border-[#CA8A04]">
                    <span className="text-[#CA8A04] font-bold">{idx + 1}</span>
                    <div>
                      <p className="text-[#09090B] font-medium">{action.action || action}</p>
                      {action.impact && (
                        <p className="text-sm text-[#52525B] mt-1">Impact: {action.impact}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Medium-term (61-90 days) */}
            <div className="bg-white border border-[#E4E4E7] p-6">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-[#16A34A] flex items-center justify-center">
                  <span className="text-white font-bold text-sm">61-90</span>
                </div>
                <h3 className="heading-4 text-[#09090B]">{t('report.roadmap.medium_term')}</h3>
              </div>
              <div className="space-y-4">
                {(roadmapData.medium_term || []).map((action, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-[#F4F4F5] border-l-4 border-[#16A34A]">
                    <span className="text-[#16A34A] font-bold">{idx + 1}</span>
                    <div>
                      <p className="text-[#09090B] font-medium">{action.action || action}</p>
                      {action.impact && (
                        <p className="text-sm text-[#52525B] mt-1">Impact: {action.impact}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {/* GAP Analysis Tab */}
          <TabsContent value="gap" className="space-y-6" data-testid="gap-analysis">
            {normalizedGap.length > 0 ? (
              normalizedGap.map((gap, idx) => (
                <div key={idx} className="bg-white border border-[#E4E4E7] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="heading-4 text-[#09090B]">{gap.category}</h3>
                    <div className="flex items-center gap-2">
                      {gap.priority && (
                        <span className={`label-text px-3 py-1 ${
                          String(gap.priority).toLowerCase().includes('high')
                            ? 'text-[#E60000] bg-[#E60000]/10'
                            : String(gap.priority).toLowerCase().includes('med')
                            ? 'text-[#D97706] bg-[#D97706]/10'
                            : 'text-[#52525B] bg-[#F4F4F5]'
                        }`}>{String(gap.priority).toUpperCase()}</span>
                      )}
                      <span className="label-text text-[#E60000] bg-[#E60000]/10 px-3 py-1">GAP</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                    <div className="p-4 bg-[#FEF2F2] border-l-4 border-[#E60000]">
                      <p className="label-text text-[#E60000] mb-2">{t('report.gap.current_state')}</p>
                      <p className="text-sm text-[#52525B]">{gap.current_state || 'N/A'}</p>
                    </div>
                    <div className="p-4 bg-[#F0FDF4] border-l-4 border-[#16A34A]">
                      <p className="label-text text-[#16A34A] mb-2">{t('report.gap.desired_state')}</p>
                      <p className="text-sm text-[#52525B]">{gap.desired_state || 'N/A'}</p>
                    </div>
                  </div>
                  {gap.gap_description && (
                    <div className="mb-4">
                      <p className="font-medium text-[#09090B] mb-1">{t('report.gap.description')}</p>
                      <p className="text-sm text-[#52525B]">{gap.gap_description}</p>
                    </div>
                  )}
                  {gap.impact && (
                    <div className="mb-4">
                      <p className="font-medium text-[#09090B] mb-1">{t('report.gap.impact')}</p>
                      <p className="text-sm text-[#52525B]">{gap.impact}</p>
                    </div>
                  )}
                  {gap.closing_actions && gap.closing_actions.length > 0 && (
                    <div>
                      <p className="font-medium text-[#09090B] mb-2">{t('report.gap.actions')}</p>
                      <ul className="space-y-2">
                        {gap.closing_actions.map((action, aidx) => (
                          <li key={aidx} className="flex items-start gap-2 text-sm text-[#52525B]">
                            <CheckCircle size={16} weight="fill" className="text-[#0F1B36] mt-0.5 flex-shrink-0" />
                            {action}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="bg-white border border-[#E4E4E7] p-8 text-center">
                <p className="text-[#52525B]">{t('report.gap.empty')}</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ReportPage;
